import { useEffect, useState, useCallback } from "react";
import Swal from 'sweetalert2';

import {
    Typography,
    CircularProgress,
    Stack,
    Box,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Skeleton,
    IconButton
} from "@mui/material";

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import RefreshIcon from '@mui/icons-material/Refresh';

import AddUser from "../components/manage_system_users/AddUser";
import EditUser from "../components/manage_system_users/EditUser";

import CustomButton from "../components/custom_components/CustomButton";
import CustomSearch from "../components/custom_components/CustomSearch";
import CustomIconButton from "../components/custom_components/CustomIconButton";

import SearchNotFound from "../utils/SearchNotFound";

const apiUrl = process.env.REACT_APP_API_URL;

export default function ManageUsers() {

    const [showAddUser, setShowAddUser] = useState(false);
    const [showEditUser, setShowEditUser] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const [search, setSearch] = useState("");

    const [user, setUser] = useState({});
    const [users, setUsers] = useState([]);

    const [loading, setLoading] = useState(false);
    // const [isDelete, setIsDelete] = useState(false);

    // DISPLAY USERS
    const handleDisplayUsers = useCallback(async () => {
        setLoading(true);

        try {
            const response = await fetch(
                `${apiUrl}manage_users/get_users.php`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );

            const result = await response.json();

            if (result.status === 'success') {
                setUsers(result.data);
            } else {
                Swal.fire({
                    text: result.message,
                    icon: 'info'
                });
            }

        } catch (error) {
            Swal.fire({
                title: 'Server Error!',
                text: error,
                icon: 'error'
            });

            console.error(error);

        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        handleDisplayUsers();
    }, [handleDisplayUsers]);

    const filtered = users.filter(
        (p) =>
            p.FullName.toLowerCase().includes(search.toLowerCase()) ||
            p.UserName.toLowerCase().includes(search.toLowerCase())
    );

    const handleEditUser = (data) => {
        setUser(data || {});
        setShowEditUser(true);
    };

    const handleDelete = async (id) => {

        const res = await Swal.fire({
            title: "Delete User?",
            text: "This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Delete",
            confirmButtonColor: "#e94d4d",
        });

        if (!res.isConfirmed) return;

        setDeletingId(id);

        try {

            const response = await fetch(
                `${apiUrl}manage_users/delete_user.php`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ id })
                }
            );

            const result = await response.json();

            if (result.status === 'success') {
                setUsers((prev) => prev.filter((p) => p.id !== id));
            } else {
                Swal.fire({
                    text: result.message,
                    icon: "error"
                });
            }

        } catch (error) {

            Swal.fire({
                title: 'Error deleting user!',
                text: error,
                icon: "error"
            });

            console.error(error);

        } finally {
            setDeletingId(null);
        }
    };

    return (
        <>
            {/* ================= HEADER ================= */}
            <Box mb={2}>
                <Typography
                    sx={{
                        fontWeight: 700,
                        fontSize: 16,
                        color: '#111',
                    }}
                >
                    System Users
                </Typography>

                <Typography
                    sx={{
                        fontSize: 13,
                        color: '#777',
                    }}
                >
                    Manage system accounts and access
                </Typography>
            </Box>

            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
            >
                <Stack direction="row" gap={2}>
                    <CustomSearch
                        md="36ch"
                        placeholder="Search by User Name"
                        value={search}
                        onChange={(val) => setSearch(val)}
                    />

                    <IconButton
                        aria-label="refresh"
                        size="small"
                        disabled={loading}
                        onClick={() => handleDisplayUsers()}
                        sx={{
                            width: 36,
                            height: 36,

                            backgroundColor: "#1976d2",
                            color: "#ffffff",

                            borderRadius: "8px",

                            transition: "all .2s ease",

                            "&:hover": {
                                backgroundColor: "#3B82F6",
                                borderColor: "#3B82F6",
                            },

                            '&:disabled': {
                                backgroundColor: '#e0e0e0', // soft gray
                                color: '#9e9e9e',           // muted text
                                boxShadow: 'none',
                            }
                        }}
                    >
                        {loading ? (
                            <CircularProgress
                                size={18}
                                thickness={5}
                                color="inherit"
                            />
                        ) : (
                            <RefreshIcon fontSize="small" />
                        )}
                    </IconButton>
                </Stack>

                <CustomButton
                    label="Add User"
                    startIcon={<PersonAddAlt1RoundedIcon />}
                    onClick={() => setShowAddUser(true)}
                />
            </Stack>

            {/* ================= TABLE ================= */}
            <Box sx={{
                border: "1px solid #E2E8F0",
                borderRadius: 1.2,
                overflow: "hidden",
            }}>
                <TableContainer sx={{ maxHeight: "100vh" }}>
                    <Table stickyHeader size="small" >
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#111', height: 44, }} >
                                {[
                                    'Full Name',
                                    'User Name',
                                    'Password',
                                    'Role',
                                    'Actions',
                                ].map((header, index) => (
                                    <TableCell key={header} align={header === "Actions" ? "center" : "left"} sx={{
                                        bgcolor: "#1C2536",
                                        color: "#94A3B8",
                                        fontWeight: 700,
                                        fontSize: 11,
                                        letterSpacing: "0.6px",
                                        textTransform: "uppercase",
                                        borderBottom: "none",
                                        py: 1,
                                        width: header === "#" ? 48 : header === "Actions" ? 110 : "auto",
                                    }}>
                                        {header}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>

                            {/* LOADING */}
                            {loading ? (

                                [...Array(3)].map((_, index) => (
                                    <TableRow index={index}>
                                        <TableCell >
                                            <Skeleton variant="text" />
                                        </TableCell>
                                        <TableCell >
                                            <Skeleton variant="text" />
                                        </TableCell>
                                        <TableCell >
                                            <Skeleton variant="text" />
                                        </TableCell>
                                        <TableCell >
                                            <Skeleton variant="text" />
                                        </TableCell>
                                        <TableCell >
                                            <Skeleton variant="rounded" />
                                        </TableCell>
                                    </TableRow>
                                ))

                            ) : filtered.length > 0 ? (

                                filtered.map((user) => (
                                    <TableRow
                                        key={user.id}
                                        sx={{
                                            '&:hover': {
                                                backgroundColor: '#dcfbff',
                                            },
                                        }}
                                    >

                                        <TableCell>
                                            <Typography
                                                sx={{
                                                    fontWeight: 600,
                                                    color: '#111',
                                                }}
                                            >
                                                {user.FullName}
                                            </Typography>
                                        </TableCell>

                                        <TableCell>
                                            {user.UserName}
                                        </TableCell>

                                        <TableCell>
                                            <Typography
                                                sx={{
                                                    color: '#777',
                                                    fontSize: 13,
                                                }}
                                            >
                                                {user.UserPass}
                                            </Typography>
                                        </TableCell>

                                        <TableCell>
                                            <Chip
                                                label={user.UserRole}
                                                size="small"
                                                variant="outlined"
                                                sx={{
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                }}
                                            />
                                        </TableCell>

                                        <TableCell align="center">
                                            <Stack direction="row" spacing={1} justifyContent="center">
                                                <CustomIconButton
                                                    type="success"
                                                    icon={<EditIcon fontSize="small" />}
                                                    onClick={() => handleEditUser(user)}
                                                />

                                                <CustomIconButton
                                                    type="delete"
                                                    loading={deletingId === user.id}
                                                    disabled={deletingId === user.id}
                                                    icon={<DeleteIcon fontSize="small" />}
                                                    onClick={() => handleDelete(user.id)}
                                                />
                                            </Stack>
                                        </TableCell>

                                    </TableRow>
                                ))

                            ) : (

                                <TableRow>
                                    <TableCell colSpan={5}>
                                        <SearchNotFound />
                                    </TableCell>
                                </TableRow>

                            )}

                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Footer */}
                {!loading && filtered.length > 0 && (
                    <Box sx={{
                        px: 2, py: 1.25,
                        borderTop: "1px solid #F1F5F9",
                        bgcolor: "#FAFAFA",
                    }}>
                        <Typography fontSize={11} color="#94A3B8">
                            Showing <strong>{filtered.length}</strong> of <strong>{users.length}</strong> system user{users.length !== 1 ? "s" : ""}
                        </Typography>
                    </Box>
                )}
            </Box>

            <AddUser
                open={showAddUser}
                onClose={() => setShowAddUser(false)}
                onRefresh={handleDisplayUsers}
            />

            <EditUser
                open={showEditUser}
                onClose={() => setShowEditUser(false)}
                onRefresh={handleDisplayUsers}
                user={user}
            />
        </>
    );
}

// Tooltip needs to be imported — add to MUI imports above:
function Tooltip({ title, children }) {
    return (
        <Box component="span" title={title}>
            {children}
        </Box>
    );
}